/**
 * Loyalty Paragraph Block - Frontend View
 *
 * This component renders on the actual frontend (not the editor).
 * It uses the enrollment store to determine the user's actual enrollment status
 * and renders the appropriate paragraph text with variable replacement.
 */
import React, { type FC } from 'react';
import { useEnrollment } from '../_shared/hooks/use-enrollment';
import { ParagraphSkeleton } from '../_shared/skeleton/patterns';
import { DynamicText } from '../_shared/dynamic-text';
import {
	createBlockHydrator,
	autoInitialize,
	datasetToBoolean,
} from '../_shared/utils/block-hydration';

// ============================================================================
// Types
// ============================================================================

interface LoyaltyParagraphAttributes {
	guestText: string;
	loggedInNotEnrolledText: string;
	enrolledText: string;
	enableAnimation?: boolean;
}

interface LoyaltyParagraphProps {
	attributes: LoyaltyParagraphAttributes;
	initialContent?: string;
}

// ============================================================================
// Component
// ============================================================================

/**
 * Frontend loyalty paragraph component
 *
 * This component now only hydrates for enrolled users to update credits dynamically.
 * For guests and logged-in-not-enrolled users, the server-rendered content is used as-is.
 *
 * IMPORTANT: We preserve the server-rendered content by returning null initially
 * (which leaves the existing HTML intact) until the enrollment status is determined.
 */
const LoyaltyParagraphView: FC<LoyaltyParagraphProps> = ({ attributes, initialContent }) => {
	const { status, credits, creditsCurrency, isLoading } = useEnrollment();

	// Track whether we've received data from the store yet
	// We only mount this component for enrolled users (server-side determined),
	// so we know the user IS enrolled - we're just waiting for fresh credit data
	const [hasReceivedData, setHasReceivedData] = React.useState(false);

	React.useEffect(() => {
		// Once we have credits data (not loading anymore and status is enrolled)
		if (!isLoading && status === 'enrolled' && credits !== undefined) {
			setHasReceivedData(true);
		}
	}, [isLoading, status, credits]);

	// Show skeleton while loading (only if we don't have initial content yet)
	if (isLoading && !hasReceivedData && !initialContent) {
		return (
			<span style={{ display: 'inline-block', minHeight: '1em', width: '100%' }}>
				<ParagraphSkeleton ariaMessage="Loading your rewards balance..." />
			</span>
		);
	}

	// Use DynamicText component for variable replacement and animation
	// Pass initialContent to prevent flash on first render
	const enableAnimation = attributes.enableAnimation ?? true;
	const enrolledText = attributes.enrolledText || '';

	return (
		<DynamicText
			text={enrolledText}
			replacements={{
				credits,
				credits_currency: creditsCurrency,
			}}
			animatedKeys={['credits']}
			enableAnimation={enableAnimation}
			initialContent={!hasReceivedData ? initialContent : undefined}
		/>
	);
};

// ============================================================================
// Initialization
// ============================================================================

/**
 * Initialize all loyalty paragraph blocks on the page
 *
 * Only hydrates blocks for enrolled users (those with data-enrollment-status="enrolled").
 * For guests and logged-in-not-enrolled users, the server-rendered content is used.
 *
 * IMPORTANT: We preserve the server-rendered content by only mounting React
 * after the enrollment store has resolved, preventing content flash.
 */
export const initLoyaltyParagraphBlocks = createBlockHydrator<LoyaltyParagraphAttributes>({
	// Only hydrate enrolled user blocks (with dynamic credits)
	selector: '[data-enrollment-status="enrolled"][data-enrolled-text]',

	extractAttributes: (element) => ({
		guestText: element.dataset.guestText || '',
		loggedInNotEnrolledText: element.dataset.loggedInNotEnrolledText || '',
		enrolledText: element.dataset.enrolledText || '',
		enableAnimation: datasetToBoolean(element.dataset.enableAnimation, true),
	}),

	render: (_element, attributes, initialContent) => (
		<LoyaltyParagraphView attributes={attributes} initialContent={initialContent} />
	),

	waitForStores: true,
	preserveInitialContent: true,
});

// Auto-initialize on DOM ready
autoInitialize(initLoyaltyParagraphBlocks);
